import React, { CSSProperties } from 'react';
import { PromoNotification } from 'reducers/user';

// Presentational notification list shared by the bell (NotificationCenter) and
// the account menu (AccountMenuPanel) — one renderer keeps the card styling,
// type badges, and relative-time formatting consistent across both surfaces.

// Saloon palette mirror — values must match src/components/SaloonLobby/
// _tokens.scss. Kept inline because these components use CSS-in-JS rather than
// CSS modules; any future change to the tokens file should be reflected here.
export const SALOON = {
  navy950: '#0a1322',
  navy900: '#0e1a2d',
  navy800: '#122339',
  orange500: '#e8821f',
  orange400: '#f59838',
  orange300: '#ffb45c',
  orange200: '#ffd089',
  bone: '#f1e3c7',
  bone2: '#d9c79e',
  ink: '#1a0e06',
  fontBody: "'Inter', system-ui, sans-serif",
  fontDecorative: "'Rye', serif",
  fontTypewriter: "'Special Elite', monospace"
};

// Envelope icon — matches the Saloon design's "inbox" treatment. Inlined so
// this module doesn't acquire an extra dependency; stroke follows currentColor
// so hover transitions work.
export const InboxIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.7'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <rect x='3' y='5' width='18' height='14' rx='2' />
    <path d='M3 7l9 6 9-6' />
  </svg>
);

// Shared stroked-icon wrapper for the notification type glyphs — matches the
// design spec: fill none, stroke currentColor (inherits the wrap's color),
// stroke-width 1.8, round caps/joins, 22×22.
const StrokeIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg
    viewBox='0 0 24 24'
    width={24}
    height={24}
    fill='none'
    stroke='currentColor'
    strokeWidth={1.8}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    {children}
  </svg>
);

export function formatRelativeTime(timestamp: number) {
  if (!timestamp) return 'Just now';

  const now = Date.now();
  const notificationTime = timestamp * 1000;
  const diffInSeconds = Math.max(1, Math.floor((now - notificationTime) / 1000));

  const timeUnits = [
    { unit: 'y', value: 365 * 24 * 60 * 60 },
    { unit: 'mo', value: 30 * 24 * 60 * 60 },
    { unit: 'd', value: 24 * 60 * 60 },
    { unit: 'h', value: 60 * 60 },
    { unit: 'm', value: 60 }
  ];

  for (const { unit, value } of timeUnits) {
    const amount = Math.floor(diffInSeconds / value);
    if (amount >= 1) {
      return `${amount}${unit} ago`;
    }
  }

  return 'Just now';
}

type NotificationType = 'cashier' | 'promo' | 'tournament' | 'support' | 'general';

export function getNotificationType(message: string): NotificationType {
  const normalized = (message || '').toLowerCase();

  if (
    normalized.includes('withdraw') ||
    normalized.includes('deposit') ||
    normalized.includes('cashier') ||
    normalized.includes('transfer') ||
    normalized.includes('wallet') ||
    normalized.includes('refund')
  ) {
    return 'cashier';
  }

  if (
    normalized.includes('promo') ||
    normalized.includes('reward') ||
    normalized.includes('bonus') ||
    normalized.includes('credited')
  ) {
    return 'promo';
  }

  if (
    normalized.includes('tournament') ||
    normalized.includes('sit & go') ||
    normalized.includes('registration') ||
    normalized.includes('rebuy')
  ) {
    return 'tournament';
  }

  if (
    normalized.includes('support') ||
    normalized.includes('ticket') ||
    normalized.includes('agent') ||
    normalized.includes('reply')
  ) {
    return 'support';
  }

  return 'general';
}

// The icon wrap is a single uniform orange treatment (see notificationIconWrapStyle),
// so this only picks the glyph per type — the color/ring come from the wrap.
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'cashier':
      // Refund / money-back: circular return arrows wrapping a dollar sign.
      return (
        <StrokeIcon>
          <path d='M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8' />
          <path d='M21 3v5h-5' />
          <path d='M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' />
          <path d='M3 21v-5h5' />
          <line x1='12' y1='8' x2='12' y2='16' />
          <path d='M14 9.5c-.5-.7-1.2-1-2-1-1.1 0-2 .6-2 1.5s.9 1.3 2 1.5 2 .6 2 1.5-.9 1.5-2 1.5c-.8 0-1.5-.3-2-1' />
        </StrokeIcon>
      );
    case 'promo':
      return (
        <StrokeIcon>
          <circle cx='12' cy='8' r='6' />
          <path d='M15.477 12.89 17 22l-5-3-5 3 1.523-9.11' />
        </StrokeIcon>
      );
    case 'tournament':
      return (
        <StrokeIcon>
          <path d='M6 9H4.5a2.5 2.5 0 0 1 0-5H6' />
          <path d='M18 9h1.5a2.5 2.5 0 0 0 0-5H18' />
          <path d='M4 22h16' />
          <path d='M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22' />
          <path d='M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22' />
          <path d='M18 2H6v7a6 6 0 0 0 12 0V2Z' />
        </StrokeIcon>
      );
    case 'support':
      return (
        <StrokeIcon>
          <circle cx='12' cy='12' r='10' />
          <path d='m4.93 4.93 4.24 4.24' />
          <path d='m14.83 9.17 4.24-4.24' />
          <path d='m14.83 14.83 4.24 4.24' />
          <path d='m9.17 14.83-4.24 4.24' />
          <circle cx='12' cy='12' r='4' />
        </StrokeIcon>
      );
    default:
      return (
        <StrokeIcon>
          <circle cx='12' cy='12' r='10' />
          <path d='M12 16v-4' />
          <path d='M12 8h.01' />
        </StrokeIcon>
      );
  }
}

interface NotificationListProps {
  notifications: PromoNotification[];
  onNotificationClick: (notification: PromoNotification) => void;
  className?: string;
}

const NotificationList: React.FC<NotificationListProps> = ({
  notifications,
  onNotificationClick,
  className
}) => {
  if (notifications.length === 0) {
    return (
      <div className={className} style={listBodyStyle}>
        <div style={emptyStateStyle}>
          <span style={{ color: SALOON.bone2, opacity: 0.4 }}>
            <InboxIcon size={32} />
          </span>
          <span style={emptyTitleStyle}>Mailbag&apos;s empty, partner.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={listBodyStyle}>
      {notifications.map((notification) => {
        const notificationType = getNotificationType(notification.message);
        const notificationIcon = getNotificationIcon(notificationType);

        return (
          <button
            key={notification.id}
            type='button'
            onClick={() => onNotificationClick(notification)}
            style={{
              ...notificationCardStyle,
              ...(notification.isRead === 0 ? unreadCardStyle : {})
            }}
          >
            <div style={notificationIconWrapStyle}>{notificationIcon}</div>
            <div style={notificationTextStyle}>
              <span style={notificationMessageStyle}>
                {notification.heading || notification.message}
              </span>
              {notification.heading && (
                <span style={notificationBodyStyle}>{notification.message}</span>
              )}
              <span style={notificationTimeStyle}>
                {formatRelativeTime(notification.timestamp)}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default NotificationList;

const listBodyStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 14,
  overflowY: 'auto'
};

// Mirrors the design's .m-apn-card: a 42px icon column + flexible text column,
// a faint top-lit white gradient (NOT a dark/orange fill), a low-alpha orange
// hairline border, and an inset highlight along the top edge.
const notificationCardStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '42px 1fr',
  gap: 12,
  padding: '12px 14px',
  background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
  border: '1px solid rgba(232, 130, 31, 0.1)',
  borderRadius: 8,
  boxShadow: 'inset 0 1px 0 rgba(255, 180, 92, 0.05)',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: SALOON.fontBody,
  color: SALOON.bone2
};

const notificationTextStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
  flex: 1
};

// Unread keeps the same .m-apn-card surface — only a stronger orange hairline
// and brighter body text mark it as unread (no heavy orange/dark fill).
const unreadCardStyle: CSSProperties = {
  border: '1px solid rgba(232, 130, 31, 0.28)',
  color: SALOON.bone
};

// Uniform icon treatment from the design's .m-apn-icon: 42px orange-ringed disc
// with a dark fill and soft orange glow. Top-aligned so it sits level with the
// card title (matches .m-apn-card's default grid alignment).
const notificationIconWrapStyle: CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: '50%',
  background: 'rgba(0, 0, 0, 0.4)',
  border: '1.5px solid rgba(232, 130, 31, 0.5)',
  color: '#ffb45c', // var(--orange-300) — glyphs inherit via currentColor
  display: 'grid',
  placeItems: 'center',
  boxShadow: '0 0 8px rgba(232, 130, 31, 0.18)',
  alignSelf: 'start',
  flexShrink: 0
};

// .m-apn-card-time
const notificationTimeStyle: CSSProperties = {
  marginTop: 2,
  fontFamily: SALOON.fontTypewriter, // Special Elite
  fontSize: '10px',
  color: SALOON.bone2,
  opacity: 0.6,
  letterSpacing: '0.12em'
};

// .m-apn-card-title
const notificationMessageStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '14px',
  color: SALOON.bone,
  letterSpacing: '0.02em',
  lineHeight: 1.2
};

// .m-apn-card-msg
const notificationBodyStyle: CSSProperties = {
  fontFamily: SALOON.fontBody, // Inter
  fontWeight: 500,
  fontSize: '12.5px',
  color: SALOON.bone2,
  opacity: 0.85,
  lineHeight: 1.45
};

const emptyStateStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  minHeight: 240,
  textAlign: 'center',
  padding: '20px 18px'
};

const emptyTitleStyle: CSSProperties = {
  fontFamily: SALOON.fontTypewriter,
  color: SALOON.bone,
  fontSize: '0.92rem',
  letterSpacing: '0.06em'
};
